import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Skeleton,
    Stack,
    Typography,
    type SxProps,
    type Theme,
    Tooltip,
    AccordionDetails,
    AccordionSummary,
    Accordion,
    Divider,
    Paper,
} from "@mui/material";
import {useAgentMetadata} from "../../hooks/useAgentMetadata";
import type {AgentTemplate, AgentTemplateMember, AgentTemplatePrompt} from "../../types/chatTypes";
import {TemplatesIcon} from "./TemplateIcons";
import LockIcon from "../../../../components/icons/svg/LockIcon.svg?react";
import DotIcon from '~/icons/svg/DotIcon.svg?react';
import ChevronIcon from "~/icons/svg/ChevronIcon.svg?react";
import CloseIcon from "~/icons/svg/CloseIcon.svg?react";
import {COLORS} from "~/style/colors-ts";


/** List label: `[Label]` → blue `/Label`; `raw` with brackets is unchanged for `onPromptSelect`. */
function ReadablePrompt({raw, sx}: {raw: string; sx?: SxProps<Theme>}) {
    const re = /\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    let slotKey = 0;
    while ((m = re.exec(raw)) !== null) {
        if (m.index > lastIndex) {
            parts.push(raw.slice(lastIndex, m.index));
        }
        const label = m[1].trim();
        parts.push(
            <Box component="span" key={`slot-${slotKey++}`} sx={{color: "#4088F8"}}>
                /{label}
            </Box>,
        );
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < raw.length) {
        parts.push(raw.slice(lastIndex));
    }
    if (parts.length === 0) {
        return <span>{raw}</span>;
    }
    return (
        <Box
            component="span"
            sx={{
                typography: "body2",
                whiteSpace: "normal",
                wordBreak: "break-word",
                ...sx,
            }}
        >
            {parts}
        </Box>
    );
}

type TemplatePromptRowProps = {
    label: string;
    prompt: string;
    onPromptClick: (prompt: string) => void;
    onPromptHover: (prompt: string|null) => void;
};

function TemplatePromptRow({label, prompt, onPromptClick, onPromptHover}: TemplatePromptRowProps) {
    const isMaterialTemplate = prompt.includes("[material]");
    return (
        <ListItemButton
            {...(!isMaterialTemplate && {
                onClick: () => onPromptClick(prompt),
            })}
            alignItems="flex-start"
            sx={{
                pb: isMaterialTemplate ? 0 : 1,
                px: 1,
                py: 1,
                "&:hover": {
                    bgcolor: "action.hover",
                    backgroundColor: isMaterialTemplate
                        ? "transparent"
                        : "action.hover",
                    cursor: isMaterialTemplate ? "default" : "pointer",
                },
                "&:not(:last-child)": {
                    borderBottom: "1px solid #C4C6D0",
                },
            }}
            onMouseEnter={() => onPromptHover(prompt)}
            onMouseLeave={() => onPromptHover(null)}
        >
            <ListItemText
                primary={
                    isMaterialTemplate ? (
                        <Stack direction="row" alignItems="center" gap="4px">
                            <Tooltip title="Upgrade to access /material entities">
                                <Box>
                                    <ReadablePrompt raw={label} sx={{opacity: 0.5}}/>
                                </Box>
                            </Tooltip>
                            <LockIcon style={{marginLeft: "auto"}}/>
                        </Stack>
                    ) : (
                        <ReadablePrompt raw={label}/>
                    )
                }
                slotProps={{
                    primary: {
                        component: "div",
                    },
                }}
            />
        </ListItemButton>
    );
}

function EllipsisTypography({text}: {text: string}) {
    const [open, setOpen] = useState(false);

    const updateOpen = (el: HTMLElement) => {
        setOpen(el.scrollWidth > el.clientWidth);
    };

    return (
        <Tooltip title={text} open={open} disableFocusListener disableTouchListener>
            <Typography
                variant="body2"
                onMouseEnter={(event) => updateOpen(event.currentTarget)}
                onMouseLeave={() => setOpen(false)}
                sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
}

type GroupedTemplatesProps = {
    members: AgentTemplateMember[];
    keyPrefix: string;
    onPromptClick: (prompt: string) => void;
    onPromptHover: (prompt: string|null) => void;
};

function GroupedTemplates({
                              members,
                              keyPrefix,
                              onPromptClick,
                              onPromptHover,
                          }: GroupedTemplatesProps) {
    const [expanded, setExpanded] = useState<string | false>(false);

    return (
        <>
            {members.map((member, index) => {
                const memberKey = `${keyPrefix}-${index}`;
                if (member.prompt) {
                    return (
                        <Stack direction="row"  alignItems="center" sx={{ml: 1, '&:not(:last-child)': {borderBottom: '1px solid #C4C6D0'}}}>
                            <DotIcon />
                            <TemplatePromptRow
                                key={memberKey}
                                label={member.title}
                                prompt={member.prompt}
                                onPromptClick={onPromptClick}
                                onPromptHover={onPromptHover}
                            />
                        </Stack>
                    );
                }
                if (!member.members?.length) {
                    return null;
                }
                return (
                    <>
                        <Accordion
                            key={memberKey}
                            expanded={expanded === memberKey}
                            onChange={(_, isExpanded) =>
                                setExpanded(isExpanded ? memberKey : false)
                            }
                            disableGutters
                            elevation={0}
                            sx={{
                                "&:before": {display: "none"},
                                backgroundColor: "transparent",
                                "&.Mui-expanded": {
                                    borderWidth: "1px",
                                    borderStyle: "solid",
                                    borderRadius: "8px",
                                    borderColor: "divider",
                                },
                            }}
                        >
                            <AccordionSummary
                                aria-controls={`${memberKey}-content`}
                                id={`${memberKey}-header`}
                                sx={{px: 1, minHeight: "44px", height: "44px", '&:hover': {bgcolor: "action.hover"}, '& .MuiAccordionSummary-content': {minWidth: 0}}}
                            >
                                <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between" width="100%">
                                    <EllipsisTypography text={member.title} />
                                    <ChevronIcon style={{transform: expanded === memberKey ? 'rotate(-90deg)' : 'rotate(90deg)'}} />
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{p: 0}}>
                                <GroupedTemplates
                                    members={member.members}
                                    keyPrefix={memberKey}
                                    onPromptClick={onPromptClick}
                                    onPromptHover={onPromptHover}
                                />
                            </AccordionDetails>
                        </Accordion>
                        {index < members.length - 1 && <Divider variant="middle" sx={{opacity: 1, mx: 1}} />}
                    </>
                );
            })}
        </>
    );
}

export type AgentTemplatesProps = {
    /** Current agent (`app_name`); templates are taken from this row in agents-metadata. */
    appName: string;
    /** Called when the user picks a prompt from the template popover (popover closes first). */
    onPromptSelect?: (prompt: string) => void;
    onPromptHover: (prompt: string | null) => void;
};

export const AgentTemplates: React.FC<AgentTemplatesProps> = ({
                                                                  appName,
                                                                  onPromptSelect,
                                                                  onPromptHover,
                                                              }) => {
    const {data: agents, isPending} = useAgentMetadata();
    const [popover, setPopover] = useState<{
        anchor: HTMLElement;
        template: AgentTemplate;
    } | null>(null);
    const chipListRef = useRef<HTMLDivElement>(null);
    const paperRef = useRef<HTMLDivElement>(null);
    /** Popper has no modal backdrop; close when clicking outside chips + panel. */
    useEffect(() => {
        if (!popover) {
            return undefined;
        }
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (paperRef.current?.contains(target)) {
                return;
            }
            if (chipListRef.current?.contains(target)) {
                return;
            }
            setPopover(null);
        };
        document.addEventListener("mousedown", handleMouseDown, true);
        return () =>
            document.removeEventListener("mousedown", handleMouseDown, true);
    }, [popover]);

    useEffect(() => {
        if (!popover) {
            return undefined;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setPopover(null);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [popover]);

    const templates = useMemo(() => {
        if (!agents?.length) {
            return [];
        }
        const agent = agents.find((a) => a.app_name === appName);
        return agent?.templates ?? [];
    }, [agents, appName]);

    const handleChipMouseDown = (
        event: React.MouseEvent<HTMLElement>,
        template: AgentTemplate,
    ) => {
        event.preventDefault();
        setPopover({anchor: event.currentTarget, template});
    };

    const handleClosePopover = () => {
        setPopover(null);
    };

    const handlePromptClick = (prompt: string) => {
        handleClosePopover();
        onPromptSelect?.(prompt);
    };

    const renderTemplateContent = (template: AgentTemplate) => {
        if (template.template_type === "two_layer") {
            return (
                <GroupedTemplates
                    members={template.members}
                    keyPrefix={template.name}
                    onPromptClick={handlePromptClick}
                    onPromptHover={onPromptHover}
                />
            );
        }
        // string[] is for the old templates
        return template.prompts.map((item:  string | AgentTemplatePrompt, index) => (
            typeof item === 'string' ?
                <TemplatePromptRow
                    key={`${template.name}-${index}`}
                    label={item}
                    prompt={item}
                    onPromptClick={handlePromptClick}
                    onPromptHover={onPromptHover} />
                :
                <TemplatePromptRow
                    key={`${template.name}-${index}`}
                    label={item.title}
                    prompt={item.prompt}
                    onPromptClick={handlePromptClick}
                    onPromptHover={onPromptHover}
                />
        ));


    };

    if (isPending) {
        return (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{mt: 2}}>
                <Skeleton variant="rounded" width={140} height={32}/>
                <Skeleton variant="rounded" width={120} height={32}/>
                <Skeleton variant="rounded" width={160} height={32}/>
            </Stack>
        );
    }

    if (!templates.length) {
        return null;
    }

    const open = Boolean(popover);

    return (
        <Box sx={{mt: 2, position: "relative"}}>
            <Box ref={chipListRef}>
                <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                    {templates.map((template) => (
                        <Chip
                            key={template.name}
                            size="medium"
                            variant="outlined"
                            label={template.title}
                            icon={<TemplatesIcon name={template.name}/>}
                            onMouseDown={(e) => handleChipMouseDown(e, template)}
                            clickable
                            aria-haspopup="listbox"
                            aria-expanded={open && popover?.template.name === template.name}
                            sx={{
                                p: 1,
                                "& .MuiChip-label": {
                                    pl: 1,
                                },
                                cursor: "pointer",
                                "&:hover": {bgcolor: "action.hover"},
                                border: "1px solid #C4C6D0",
                            }}
                        />
                    ))}
                </Stack>
            </Box>

            {open && popover && <Paper
                ref={paperRef}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: "divider",
                    py: 1.5,
                    px: 2,
                    position: "absolute",
                    top: 0,
                    width: "100%",
                    zIndex: 1000,
                    backgroundColor: COLORS.white[700],
                }}
            >
                <Stack direction="row" alignItems="center" sx={{mb: 1, color: '#74777F'}} gap={1}>
                    <TemplatesIcon name={popover.template.name} />
                    <Typography variant="caption" component="h3">{popover.template.title}</Typography>
                    <CloseIcon onClick={handleClosePopover} style={{cursor: 'pointer', marginLeft: 'auto'}} width={18} height={18} />
                </Stack>
                <List dense disablePadding sx={{py: 0}}>
                    {renderTemplateContent(popover.template)}
                </List>
            </Paper>}
        </Box>
    );
};
